import type { ComponentChildren, VNode } from 'preact';
import { useLocation } from 'preact-iso';
import './NavItem.css';

interface Props {
  href: string;
  children: ComponentChildren;
  /** Optional small glyph (16-20 px SVG) shown at the left of the row. When
   *  the sidebar is collapsed only the icon is visible; the label collapses
   *  via CSS. The label text is preserved as the anchor's `title` attribute
   *  so a tooltip appears on hover in collapsed mode. */
  icon?: VNode;
  disabled?: boolean;
  /** Unterpunkt des Eintrags darueber: eingerueckt, im eingeklappten Zustand
   *  nur am Symbol erkennbar. */
  nested?: boolean;
}

/** Sidebar nav row. Active when the current path starts with `href` (so
 *  `/plants/abc123` still highlights the "Plants" entry). */
export function NavItem({ href, children, icon, disabled, nested }: Props) {
  const location = useLocation();
  // Exact match for '/', prefix match for everything else. Without this the
  // root nav item would stay highlighted on every subpage.
  const active = !disabled && (href === '/'
    ? location.path === '/'
    : location.path === href || location.path.startsWith(href + '/'));
  const classes = ['hv-nav-item'];
  if (active) classes.push('hv-nav-item--active');
  if (disabled) classes.push('hv-nav-item--disabled');
  if (nested) classes.push('hv-nav-item--nested');
  // The label goes through `title` too so tooltip reveals it in the
  // collapsed-icon layout. Best-effort — only serialises to text if the
  // caller passed a string; complex nodes fall back to no title.
  const titleText = typeof children === 'string' ? children : undefined;
  return (
    <a
      href={disabled ? undefined : href}
      class={classes.join(' ')}
      aria-current={active ? 'page' : undefined}
      aria-disabled={disabled ? 'true' : undefined}
      title={titleText}
      onClick={disabled ? (e) => e.preventDefault() : undefined}
    >
      {icon && <span class="hv-nav-item__icon" aria-hidden="true">{icon}</span>}
      <span class="hv-nav-item__label">{children}</span>
    </a>
  );
}
